/**
 * Policy cascade (§2.3): org → subtree → seat → metric; nearest scope wins.
 * Pure resolution over pre-loaded policy rows.
 */

export type PolicyScopeType = "org" | "subtree" | "seat" | "metric";

export interface PolicyRow {
  scopeType: PolicyScopeType;
  scopeId: string | null;
  key: string;
  value: unknown;
  effectiveFrom?: Date | null;
  effectiveTo?: Date | null;
}

export interface PolicyContext {
  /** seat ancestry, nearest first (the seat itself, its parent, ... root) */
  seatChain: string[];
  metricId?: string;
  at?: Date;
}

export interface Thresholds {
  great: number;
  regular: number;
  low: number;
}

export const DEFAULTS = {
  kpi_thresholds: { great: 100, regular: 80, low: 70 } as Thresholds,
  goal_thresholds: { great: 100, regular: 85, low: 70 } as Thresholds,
  own_metrics_weight: 1.0,
  insufficient_data_cutoff: 0.6,
  attainment_cap: 1.5,
  gap_policy: "excuse" as "excuse" | "count",
  lock_policy: "allow_flagged" as "hard_lock" | "allow_flagged" | "admin_only",
  // entry windows: offsets relative to period start/end, in hours
  "entry_window.daily": { opensAtStart: true, closesAfterEndHours: 0, graceHours: 12 },
  "entry_window.weekly": { opensAtStart: true, closesAfterEndHours: 0, graceHours: 24 },
  "entry_window.monthly": { opensAtStart: true, closesAfterEndHours: 0, graceHours: 48 },
  "entry_window.quarterly": { opensAtStart: true, closesAfterEndHours: 0, graceHours: 72 },
  "entry_window.yearly": { opensAtStart: true, closesAfterEndHours: 0, graceHours: 168 },
};

function active(row: PolicyRow, at?: Date): boolean {
  if (!at) return true;
  if (row.effectiveFrom && row.effectiveFrom > at) return false;
  if (row.effectiveTo && row.effectiveTo < at) return false;
  return true;
}

/** Resolve one policy key. Precedence: metric > seat > subtree (nearest ancestor) > org > default. */
export function resolvePolicy<T>(
  rows: PolicyRow[],
  key: string,
  ctx: PolicyContext,
): T | undefined {
  const rel = rows.filter((r) => r.key === key && active(r, ctx.at));
  if (ctx.metricId) {
    const m = rel.find((r) => r.scopeType === "metric" && r.scopeId === ctx.metricId);
    if (m) return m.value as T;
  }
  if (ctx.seatChain.length > 0) {
    const seatSelf = rel.find((r) => r.scopeType === "seat" && r.scopeId === ctx.seatChain[0]);
    if (seatSelf) return seatSelf.value as T;
    for (const ancestor of ctx.seatChain) {
      const sub = rel.find((r) => r.scopeType === "subtree" && r.scopeId === ancestor);
      if (sub) return sub.value as T;
    }
  }
  const org = rel.find((r) => r.scopeType === "org");
  if (org) return org.value as T;
  return (DEFAULTS as Record<string, unknown>)[key] as T | undefined;
}
